import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Modal } from 'antd';
import {
  BankOutlined,
  IdcardOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { SectionSubnav } from '../../../../../core/components';
import { PRIVATE_ROUTE_PATHS, route } from '../../../../../constants/routes';
import CandidatesTable from '../../candidate/components/CandidatesTable';
import { useProfile } from '../../profile/components/ProfileContext';
import ClientsTable from './ClientsTable';
import CompanyProfile from './CompanyProfile';
import CompanyWorkspaceHeader from './CompanyWorkspaceHeader';
import UsersTable from './UsersTable';
import { useCompanyWorkspace } from './useCompanyWorkspace';
import './Company.css';

const Company = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useProfile();
  const [activeTab, setActiveTab] = useState('1');
  const [editing, setEditing] = useState(false);
  const profileDirtyRef = useRef(false);
  const clientsTableRef = useRef<{ refresh: () => Promise<void> }>(null);
  const usersTableRef = useRef<{ refresh: () => Promise<void> }>(null);
  const candidatesTableRef = useRef<{ refresh: () => Promise<void> }>(null);
  const {
    company,
    hasPartialMetricError,
    logo,
    metrics,
    reload,
    setCompany,
    setLogo,
    workspaceState,
  } = useCompanyWorkspace();

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/clients') || path.includes('/company/clients')) {
      setActiveTab('2');
    } else if (path.includes('/users') || path.includes('/company/users')) {
      setActiveTab('3');
    } else if (path.includes('/candidates')) {
      setActiveTab('4');
    } else {
      setActiveTab('1');
    }
  }, [location.pathname]);

  const selectTab = useCallback(
    (key: string) => {
      const changeSection = () => {
        if (key === 'jobs') {
          navigate(route(PRIVATE_ROUTE_PATHS.jobs));
          return;
        }
        setActiveTab(key);
        if (key !== '1') {
          setEditing(false);
          profileDirtyRef.current = false;
        }
      };

      if (profileDirtyRef.current && key !== '1') {
        Modal.confirm({
          title: 'Leave without saving?',
          content: 'Your company profile has unsaved changes.',
          okText: 'Leave and discard',
          cancelText: 'Keep editing',
          okButtonProps: { danger: true },
          onOk: changeSection,
        });
        return;
      }
      changeSection();
    },
    [navigate]
  );

  const openEditor = () => {
    setActiveTab('1');
    profileDirtyRef.current = false;
    setEditing(true);
  };

  const handleEditingChange = useCallback((nextEditing: boolean) => {
    setEditing(nextEditing);
    if (!nextEditing) profileDirtyRef.current = false;
  }, []);

  const handleDirtyChange = useCallback((dirty: boolean) => {
    profileDirtyRef.current = dirty;
  }, []);

  useEffect(() => {
    const handleRefresh = async () => {
      if (candidatesTableRef.current?.refresh) {
        await candidatesTableRef.current.refresh();
      }
    };
    window.addEventListener('refreshCandidatesTable', handleRefresh);
    return () =>
      window.removeEventListener('refreshCandidatesTable', handleRefresh);
  }, []);

  if (workspaceState === 'error') {
    return (
      <main className="company-premium">
        <Alert
          type="error"
          showIcon
          message="We couldn’t load the company workspace"
          description="Check your connection and try again."
          action={
            <button
              className="company-secondary-action"
              type="button"
              onClick={reload}
            >
              Try again
            </button>
          }
        />
      </main>
    );
  }

  if (workspaceState === 'loading') {
    return (
      <main
        className="company-premium company-skeleton"
        aria-label="Loading company workspace"
        aria-busy="true"
      >
        <div className="company-skeleton__heading" />
        <div className="company-skeleton__identity" />
        <div className="company-skeleton__metrics" />
        <div className="company-skeleton__content" />
      </main>
    );
  }

  const items = [
    {
      key: '1',
      label: 'Company Profile',
      icon: <BankOutlined />,
      children: (
        <>
          <CompanyWorkspaceHeader
            company={company}
            editing={editing}
            isAdmin={isAdmin}
            logo={logo}
            metrics={metrics}
            onEdit={openEditor}
            onSelectSection={selectTab}
          />

          {hasPartialMetricError ? (
            <Alert
              className="company-metric-alert"
              type="warning"
              showIcon
              message="Some workspace totals are temporarily unavailable"
              action={
                <button
                  className="company-text-action"
                  type="button"
                  onClick={reload}
                >
                  Refresh
                </button>
              }
            />
          ) : null}

          <CompanyProfile
            company={company}
            editing={editing}
            logo={logo}
            metrics={metrics}
            onCompanyChange={setCompany}
            onDirtyChange={handleDirtyChange}
            onEditingChange={handleEditingChange}
            onLogoChange={setLogo}
          />
        </>
      ),
    },
    {
      key: '2',
      label: 'Clients',
      icon: <TeamOutlined />,
      children: <ClientsTable ref={clientsTableRef} />,
    },
    {
      key: '3',
      label: 'Users',
      icon: <UserOutlined />,
      children: <UsersTable ref={usersTableRef} />,
    },
    {
      key: '4',
      label: 'Candidates',
      icon: <IdcardOutlined />,
      children: <CandidatesTable ref={candidatesTableRef} />,
    },
  ];

  return (
    <main className="company-premium">
      <SectionSubnav
        activeKey={activeTab}
        onChange={selectTab}
        items={items}
        ariaLabel="Company sections"
      />
    </main>
  );
};

export default Company;
